// Adventure voxel scene — swaps the home kingdom out for a procedural biome
// (forest / mountain / coast) when an adventure begins, and swaps back on
// end. The cat (hero) stays in the same coordinate space — we just hide the
// home objects and show a biome group at the origin, then reposition the cat
// to a spawn tile. The orthographic follow-camera (heroController) keeps
// tracking the cat, so no camera surgery is needed.
//
// Transition: a full-screen black overlay fades in, the scene is swapped
// while it's opaque, then it fades back out — selling the "ported to another
// environment" feel without a hard cut.
//
// Discovery nodes: each biome scatters glowing voxels that bob in place.
// When AdventureDiscovery fires, one node pops (the cat "found" it). Purely
// cosmetic feedback — the actual payout is driven by adventureManager's timer.

import * as THREE from 'three'
import gsap from 'gsap'
import { Events, on } from '../ui/eventBus.js'
import { AdventureConfig } from '../config/balance.js'

let sceneRef = null
let heroRef = null
let active = false   // an adventure is underway (enter→exit)
let swapped = false  // the biome is actually shown (set at peak-black, not at enter)

let biomeGroup = null            // the currently-shown biome group
const biomeCache = {}            // biome name → built THREE.Group (lazy)
// Objects we toggled off. A Set (not array) so the per-frame sweep can add
// stragglers — e.g. raiders spawned mid-adventure — without duplicates.
let hiddenHomeObjects = new Set()
let savedHeroPos = null          // restore the cat here on exit
let discoveryNodes = []          // remaining glowing voxels in the active biome
let bobTimeline = null           // gsap loop driving node bobbing

// ── Shared geometries / materials (created once, reused per biome) ──────────
const GEO = {
  box: new THREE.BoxGeometry(1, 1, 1),
  cone: new THREE.ConeGeometry(0.5, 1, 6),
  ground: new THREE.BoxGeometry(12, 0.2, 12),
}

function mat(color, opts = {}) {
  return new THREE.MeshLambertMaterial({ color, ...opts })
}

// Deterministic-ish scatter without Math.random monoculture: a small LCG so
// each biome looks consistent run-to-run (nice for screenshots) but distinct
// between biomes via the seed.
function makeRng(seed) {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 0xffffffff
  }
}

export function createAdventureScene({ scene, hero }) {
  sceneRef = scene
  heroRef = hero

  on(Events.AdventureStart, ({ adventure }) => {
    const def = AdventureConfig.find((a) => a.id === adventure?.id)
    if (def) enter(def.biome)
  })
  on(Events.AdventureEnd, () => exit())
  on(Events.AdventureDiscovery, () => popDiscoveryNode())

  return { enter, exit, isActive: () => active }
}

// Per-frame sweep, called from the render loop. While an adventure is active,
// hide any top-level object that snuck into the scene after enter (raiders
// spawned by an in-progress raid, freshly-built effects, etc.) so the biome
// stays clean. Cheap: one shallow pass over scene.children.
export function tickAdventureScene() {
  if (!swapped || !sceneRef) return
  for (const child of sceneRef.children) {
    if (child === heroRef || child.isLight) continue
    if (child.userData?.isBiome) continue
    if (child.visible) {
      hiddenHomeObjects.add(child)
      child.visible = false
    }
  }
}

// ── Enter / exit ────────────────────────────────────────────────────────────

function enter(biome) {
  if (active) return
  active = true
  withFade(() => {
    hideHome()
    biomeGroup = getBiome(biome)
    biomeGroup.visible = true
    rebuildDiscoveryNodes(biomeGroup)
    startBob()
    // Save the cat's home spot, drop it at the biome spawn (origin-ish).
    if (heroRef) {
      savedHeroPos = heroRef.position.clone()
      heroRef.position.set(0, 0.05, 2)
      heroRef.rotation.y = Math.PI
    }
    swapped = true // begin per-frame straggler sweeps now that we're black
  })
}

function exit() {
  if (!active) return
  active = false
  swapped = false // stop the sweep immediately so restore isn't undone
  withFade(() => {
    if (biomeGroup) biomeGroup.visible = false
    biomeGroup = null
    stopBob()
    discoveryNodes = []
    restoreHome()
    if (heroRef && savedHeroPos) {
      heroRef.position.copy(savedHeroPos)
      savedHeroPos = null
    }
  })
}

// Hide every top-level home object that's currently visible — except the
// hero, the lights, and any biome group. Capturing only *visible* objects
// means restore won't accidentally reveal things that were conditionally
// hidden (e.g. for-sale signs).
function hideHome() {
  hiddenHomeObjects = new Set()
  for (const child of sceneRef.children) {
    if (child === heroRef) continue
    if (child.isLight) continue
    if (child.userData?.isBiome) continue
    if (child.visible) {
      hiddenHomeObjects.add(child)
      child.visible = false
    }
  }
}

// Restore every object we hid. Objects that were removed from the scene
// mid-adventure (e.g. a raider that died) are harmless to flip — they're
// orphaned. Objects still live (e.g. a raid in progress when the cat
// returns) become visible again, so the player walks back into the action.
function restoreHome() {
  for (const o of hiddenHomeObjects) {
    if (o.parent) o.visible = true
  }
  hiddenHomeObjects = new Set()
}

// ── Biome builders ──────────────────────────────────────────────────────────

function getBiome(biome) {
  if (biomeCache[biome]) return biomeCache[biome]
  const group =
    biome === 'mountain' ? buildMountain()
    : biome === 'coast'  ? buildCoast()
    : buildForest()
  group.userData.isBiome = true
  group.visible = false
  sceneRef.add(group)
  biomeCache[biome] = group
  return group
}

function groundPlane(color) {
  const g = new THREE.Mesh(GEO.ground, mat(color))
  g.position.y = -0.1
  return g
}

function box(color, w, h, d, x, y, z) {
  const m = new THREE.Mesh(GEO.box, mat(color))
  m.scale.set(w, h, d)
  m.position.set(x, y + h / 2, z)
  return m
}

function cone(color, r, h, x, z) {
  const m = new THREE.Mesh(GEO.cone, mat(color))
  m.scale.set(r * 2, h, r * 2)
  m.position.set(x, h / 2, z)
  return m
}

function buildForest() {
  const g = new THREE.Group()
  g.add(groundPlane(0x2f6b3a))
  const rng = makeRng(101)
  // Scatter trees, avoiding a clearing around the spawn (0,2).
  for (let i = 0; i < 22; i++) {
    const x = (rng() - 0.5) * 11
    const z = (rng() - 0.5) * 11
    if (Math.hypot(x - 0, z - 2) < 1.5) continue
    const h = 0.5 + rng() * 0.5
    g.add(box(0x6b4a2a, 0.16, h, 0.16, x, 0, z)) // trunk
    // Stacked canopy boxes for a chunky voxel look.
    const leaf = 0x2e8b45 + (Math.floor(rng() * 3) * 0x001500)
    g.add(box(leaf, 0.5, 0.4, 0.5, x, h, z))
    g.add(box(leaf, 0.35, 0.3, 0.35, x, h + 0.35, z))
  }
  // A few mushrooms for color.
  for (let i = 0; i < 6; i++) {
    const x = (rng() - 0.5) * 10
    const z = (rng() - 0.5) * 10
    g.add(box(0xf0ede0, 0.05, 0.12, 0.05, x, 0, z))
    g.add(box(0xc0392b, 0.16, 0.08, 0.16, x, 0.12, z))
  }
  return g
}

function buildMountain() {
  const g = new THREE.Group()
  g.add(groundPlane(0x6a665c))
  const rng = makeRng(202)
  for (let i = 0; i < 16; i++) {
    const x = (rng() - 0.5) * 11
    const z = (rng() - 0.5) * 11
    if (Math.hypot(x - 0, z - 2) < 1.6) continue
    const h = 0.8 + rng() * 2.2
    const r = 0.4 + rng() * 0.5
    g.add(cone(0x787067, r, h, x, z))
    // Snow cap on the taller peaks.
    if (h > 2) g.add(cone(0xe8eef2, r * 0.45, h * 0.3, x, z))
  }
  // Scattered boulders.
  for (let i = 0; i < 8; i++) {
    const x = (rng() - 0.5) * 10
    const z = (rng() - 0.5) * 10
    const s = 0.2 + rng() * 0.3
    g.add(box(0x8a8278, s, s, s, x, 0, z))
  }
  return g
}

function buildCoast() {
  const g = new THREE.Group()
  // Sand base.
  g.add(groundPlane(0xd9c48a))
  // Water slab covering the far half (z < -1), slightly raised + translucent.
  const water = new THREE.Mesh(GEO.box, mat(0x2f7fb0, { transparent: true, opacity: 0.8 }))
  water.scale.set(12, 0.18, 6)
  water.position.set(0, -0.02, -4)
  g.add(water)
  const rng = makeRng(303)
  // Palm trees on the sand side (z > 0).
  for (let i = 0; i < 7; i++) {
    const x = (rng() - 0.5) * 10
    const z = 0.5 + rng() * 4.5
    if (Math.hypot(x - 0, z - 2) < 1.3) continue
    const h = 0.7 + rng() * 0.5
    g.add(box(0x7a5230, 0.13, h, 0.13, x, 0, z))
    // Frond fan — four green boxes splayed from the top.
    for (const [fx, fz] of [[0.3, 0], [-0.3, 0], [0, 0.3], [0, -0.3]]) {
      g.add(box(0x3aa05a, 0.3, 0.06, 0.3, x + fx, h, z + fz))
    }
  }
  // Rocks at the waterline.
  for (let i = 0; i < 6; i++) {
    const x = (rng() - 0.5) * 11
    const z = -1 - rng() * 3
    const s = 0.2 + rng() * 0.35
    g.add(box(0x6b6b6b, s, s * 0.7, s, x, 0, z))
  }
  return g
}

// ── Discovery nodes ──────────────────────────────────────────────────────────

function rebuildDiscoveryNodes(group) {
  // Clear any leftover nodes from a prior visit to this cached biome.
  discoveryNodes.forEach((n) => n.parent?.remove(n))
  discoveryNodes = []
  const rng = makeRng(777)
  for (let i = 0; i < 8; i++) {
    const x = (rng() - 0.5) * 10
    const z = (rng() - 0.5) * 10
    if (Math.hypot(x - 0, z - 2) < 1.2) continue
    const node = new THREE.Mesh(
      GEO.box,
      new THREE.MeshLambertMaterial({ color: 0xffd97a, emissive: 0x6b5410, emissiveIntensity: 0.6 }),
    )
    node.scale.setScalar(0.22)
    node.position.set(x, 0.4, z)
    node.userData.baseY = 0.4
    node.userData.phase = rng() * Math.PI * 2
    group.add(node)
    discoveryNodes.push(node)
  }
}

// One node pops with a sparkle when the cat discovers something.
function popDiscoveryNode() {
  if (!discoveryNodes.length) return
  const node = discoveryNodes.pop()
  gsap.killTweensOf(node.scale)
  gsap.killTweensOf(node.position)
  gsap.to(node.position, { y: node.position.y + 0.8, duration: 0.5, ease: 'power2.out' })
  gsap.to(node.scale, {
    x: 0.01, y: 0.01, z: 0.01,
    duration: 0.5,
    ease: 'back.in(2)',
    onComplete: () => node.parent?.remove(node),
  })
}

// Bob loop — a single gsap ticker that floats every node sinusoidally.
function startBob() {
  stopBob()
  const state = { t: 0 }
  bobTimeline = gsap.to(state, {
    t: Math.PI * 2,
    duration: 3,
    ease: 'none',
    repeat: -1,
    onUpdate: () => {
      for (const n of discoveryNodes) {
        n.position.y = n.userData.baseY + Math.sin(state.t + n.userData.phase) * 0.12
        n.rotation.y += 0.02
      }
    },
  })
}

function stopBob() {
  if (bobTimeline) { bobTimeline.kill(); bobTimeline = null }
}

// ── Fade transition ──────────────────────────────────────────────────────────

let fadeEl = null
function ensureFadeEl() {
  if (fadeEl && document.body.contains(fadeEl)) return fadeEl
  fadeEl = document.createElement('div')
  fadeEl.className = 'adv-scene-fade'
  document.body.appendChild(fadeEl)
  return fadeEl
}

// Fade to black, run the swap at peak opacity, fade back.
function withFade(swap) {
  const el = ensureFadeEl()
  gsap.killTweensOf(el)
  gsap.to(el, {
    opacity: 1,
    duration: 0.22,
    ease: 'power1.in',
    onComplete: () => {
      try { swap() } catch (err) { console.error('[adventureScene] swap failed:', err) }
      gsap.to(el, { opacity: 0, duration: 0.32, ease: 'power1.out', delay: 0.05 })
    },
  })
}
